/**
 * The access plane, as this app talks to it (ORDR-94).
 *
 * THE PATH FAMILY IS BARE `access/`, NOT `auth/access/`, and that is measured
 * rather than assumed. The gateway handles `/api/v1/auth/*` explicitly and
 * sends everything else to a catch-all proxy; probed on 2026-09-23,
 * `GET /api/v1/access/grants` answers 401 (routed, gated) while
 * `/api/v1/access/catalog` answers 404 because AUTH-978 is not deployed yet.
 * A 404 and a 401 are the two answers that tell those apart, which is why the
 * probe was worth running: the invitation endpoints in `screens/InviteVerify`
 * DO need the `auth/` prefix, so the two families genuinely differ.
 *
 * CREDENTIALS ON EVERY CALL. The `paio_sid` cookie is host-only on
 * gate.pirouz.ai and `SameSite=Lax`; cross-ORIGIN but same-SITE with
 * ordering.pirouz.ai, so it rides a `credentials: "include"` request and
 * nothing else. Without it every call here is the 401 above.
 *
 * THIN ON PURPOSE. No retry, no cache, no normalising of the server's field
 * names. The server owns the shapes (`src/access/types.ts` mirrors them) and
 * owns every authorization decision; this module is a transport.
 */

import { API_BASE } from "../config/env.js";
import type { CatalogRole, Grant, RosterEntry, Scope } from "./types.js";

/** No trailing slash: the paths below supply their own. */
const ACCESS_BASE = `${API_BASE.replace(/\/+$/, "")}/access`;

export const ORDERING_APP = "ordering";

export interface CreateGrantInput {
  readonly user_id: string;
  readonly app: string;
  readonly role_key: string;
  readonly scope_type: string;
  readonly scope_id: string;
}

/** What the panel needs to render, and the error it shows when it cannot. */
export interface AccessApi {
  /**
   * The caller's OWN grants in `app`, which is what naming only the app returns.
   *
   * paio-auth's `list_grants` has three visibilities and picks by what the
   * request NAMES: operator sees everything; an org-admin sees every grant at a
   * concrete scope they manage, but ONLY when that scope is named; otherwise it
   * falls through to My Access and answers with the caller's own grants alone.
   * So this call is the scope-discovery read, never the roster.
   */
  listGrants(app: string): Promise<readonly Grant[]>;

  /**
   * EVERY grant at one concrete scope, each with its holder: the roster
   * (ORDR-120, `GET /access/roster`).
   *
   * All three parameters are required, and naming the scope is what asks the
   * org-admin question (ORDR-103). Only call it for a scope the caller
   * manages: the server authorizes it with `list_grants`, which raises rather
   * than downgrading, so an unmanaged scope is a 403 and the client's fault.
   * It replaced the scope-named `GET /access/grants`, which carried no holder
   * and so could show neither a name nor a last sign-in.
   */
  listRoster(app: string, scope: Scope): Promise<readonly RosterEntry[]>;
  listCatalog(app: string): Promise<readonly CatalogRole[]>;
  createGrant(input: CreateGrantInput): Promise<Grant>;
  revokeGrant(grantId: string): Promise<void>;
}

async function request<T>(
  fetchImpl: typeof fetch,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetchImpl(`${ACCESS_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const body: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = (body as { detail?: string }).detail;
    throw new Error(detail ?? `Request failed (${String(response.status)}).`);
  }
  return (body as { data: T }).data;
}

/**
 * Build a client. The client is injected into the panel in tests, and
 * `fetchImpl` is injected here so a test can assert the real request without
 * mocking `globalThis.fetch`.
 *
 * The global `fetch` is read AT CALL TIME, not captured here, because the
 * default client below is built at import.
 */
export function createAccessApi(fetchImpl?: typeof fetch): AccessApi {
  const send = <T>(path: string, init?: RequestInit): Promise<T> =>
    request<T>(fetchImpl ?? ((input, options) => fetch(input, options)), path, init);
  return {
    async listGrants(app) {
      const data = await send<{ grants: Grant[] }>(
        `/grants?app=${encodeURIComponent(app)}`,
      );
      return data.grants;
    },
    async listRoster(app, scope) {
      const query =
        `/roster?app=${encodeURIComponent(app)}` +
        `&scope_type=${encodeURIComponent(scope.scopeType)}` +
        `&scope_id=${encodeURIComponent(scope.scopeId)}`;
      const data = await send<{ entries: RosterEntry[] }>(query);
      return data.entries;
    },
    async listCatalog(app) {
      const data = await send<{ app: string; roles: CatalogRole[] }>(
        `/catalog?app=${encodeURIComponent(app)}`,
      );
      return data.roles;
    },
    createGrant(input) {
      return send<Grant>("/grants", { method: "POST", body: JSON.stringify(input) });
    },
    async revokeGrant(grantId) {
      await send<unknown>(`/grants/${encodeURIComponent(grantId)}`, { method: "DELETE" });
    },
  };
}

export const accessApi: AccessApi = createAccessApi();
